import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { getCompanies, removeCompany } from '$/store/company/actions';
import { AppState } from '$/store';
import * as Bixby from '$/core';
import './index.scss';
import CreateCompany from './add-company/CreateCompany';
import BulkImportCompanies from './bulk-import-companies';
import ImportLFIIds from './import-lfi-ids';
import HeadingBlock from '$/components/global/heading';
import DeleteButton from '$/components/grid/DeleteButton';
import { Link } from 'react-router-dom';
import Loading from '$/components/loading/Loading';
import NoDataFound from '$/components/global/noData';
import Modal from '$/components/global/modal';
import BixButton from '$/components/global/bixButton';
import { getAllPrivateAdminCompaniesState } from '$/store/company/selectors';
import { AgGridReact } from 'ag-grid-react';
import { GridApi, ColumnApi } from 'ag-grid-enterprise';
import { getIsCurrentUserCompanyMNPIInfoAdminState } from '$/store/profile/selectors';

interface ICompanyProps {
  isLoading: boolean;
  error?: Bixby.Models.BixbyError;
  companies: Bixby.Models.AdminCompanySlim[] | undefined;
  isCompanyMNPIInfoAdmin: boolean;
  dispatchGetCompanies: () => Promise<void>;
  dispatchRemoveCompany: (companySlim: Bixby.Models.AdminCompanySlim) => Promise<void>;
}

const CompanyList: React.FC<ICompanyProps> = props => {
  const { companies, dispatchGetCompanies, dispatchRemoveCompany, isLoading, isCompanyMNPIInfoAdmin } = props;
  const [isCreatingCompany, setIsCreatingCompany] = useState(false);
  const [isImportingCompanies, setIsImportingCompanies] = useState(false);
  const [isImportingLFIIds, setIsImportingLFIIds] = useState(false);
  const [deleteModalActive, setDeleteModalActive] = useState(false);
  const [companyToDelete, setCompanyToDelete] = useState({} as Bixby.Models.AdminCompanySlim | undefined);
  const [, setGridApi] = useState(null as GridApi | null);
  const [, setGridColumnApi] = useState(null as ColumnApi | null);

  useEffect(() => {
    const getCompaniesDispatcher = async () => {
      await dispatchGetCompanies();
      setIsCreatingCompany(false);
    };
    getCompaniesDispatcher();
  }, [dispatchGetCompanies]);

  const handleDeleteCompany = async () => {
    if (companyToDelete && !Bixby.Utils.isNullOrEmpty(companyToDelete)) {
      await dispatchRemoveCompany(companyToDelete);
      await dispatchGetCompanies();
      setCompanyToDelete(undefined);
    }
    setDeleteModalActive(false);
  };

  const handleOpenAddModal = (ev: React.MouseEvent<HTMLButtonElement>) => {
    ev.preventDefault();
    setIsCreatingCompany(true);
  };

  const handleOpenImportModal = (ev: React.MouseEvent<HTMLButtonElement>) => {
    ev.preventDefault();
    setIsImportingCompanies(true);
  };

  const handleCloseImportModal = () => setIsImportingCompanies(false);

  const handleImportSubmit = async () => {
    setIsImportingCompanies(false);
    await dispatchGetCompanies();
  };

  const handleOpenLFIImportModal = (ev: React.MouseEvent<HTMLButtonElement>) => {
    ev.preventDefault();
    setIsImportingLFIIds(true);
  };
  const handleCloseLFIImportModal = () => setIsImportingLFIIds(false);
  const handleLFIImportSubmit = async () => {
    setIsImportingLFIIds(false);
    await dispatchGetCompanies();
  };

  const handleCloseModal = () => {
    setIsCreatingCompany(false);
    setDeleteModalActive(false);
    setCompanyToDelete(undefined);
  };

  const onGridReady = (params: any) => {
    setGridApi(params.api);
    setGridColumnApi(params.columnApi);
  };

  const deleteButtonCellRenderer = (props: any) => {
    const handleDeleteButton = () => {
      setCompanyToDelete(props.data);
      setDeleteModalActive(true);
      return Promise.resolve();
    };
    return <DeleteButton row={props.data} deleter={handleDeleteButton} />;
  };
  const companyCellRenderer = (props: any) => (
    <Link className="link_underline" to={`/admin/company/${props.data.id}`}>
      {props.data.name}
    </Link>
  );
  const primaryAccessCellRenderer = (props: any) => (
    <Link className="link_underline" to={`/admin/client/${props.data.primaryAccess_Id}`}>
      {props.data.primaryAccess_Name}
    </Link>
  );
  const secondaryAccessCellRenderer = (props: any) => (
    <Link className="link_underline" to={`/admin/client/${props.data.secondaryAccess_Id}`}>
      {props.data.secondaryAccess_Name}
    </Link>
  );

  if (isLoading) {
    return <Loading pageLevel />;
  } else {
    return (
      <div className="company_list">
        <HeadingBlock
          title={'Manage Companies'}
          addButton={{ enabled: !isCompanyMNPIInfoAdmin, text: 'Add New Company', onClick: handleOpenAddModal }}
          importButton={{ enabled: !isCompanyMNPIInfoAdmin, text: 'Bulk Import', onClick: handleOpenImportModal }}
          secondaryImportButton={{
            enabled: !isCompanyMNPIInfoAdmin,
            text: 'Import LFI IDs',
            onClick: handleOpenLFIImportModal,
          }}
        />
        {companies && !Bixby.Utils.isNullOrEmpty(companies) ? (
          <div className="grid_container">
            <div className="grid ag-theme-alpine">
              <AgGridReact
                defaultColDef={{
                  flex: 1,
                  minWidth: 100,
                  sortable: true,
                  resizable: true,
                  lockPinned: true,
                  filter: 'agNumberColumnFilter',
                  cellClass: 'align-center',
                }}
                onGridReady={onGridReady}
                rowData={companies}
                suppressDragLeaveHidesColumns={true}
                suppressMovableColumns={true}
                animateRows={true}
                rowHeight={35}
                headerHeight={35}
                components={{
                  deleteButtonCellRenderer,
                  companyCellRenderer,
                  primaryAccessCellRenderer,
                  secondaryAccessCellRenderer,
                }}
                columnDefs={[
                  {
                    headerName: '',
                    cellClass: 'align-left',
                    cellRenderer: 'deleteButtonCellRenderer',
                    maxWidth: 65,
                  },
                  {
                    field: 'name',
                    headerName: 'Company Name',
                    width: 200,
                    filter: 'agTextColumnFilter',
                    cellClass: 'align-left',
                    cellRenderer: 'companyCellRenderer',
                  },
                  {
                    field: 'bixbyId',
                    headerName: 'BixbyId',
                    width: 250,
                    filter: 'agTextColumnFilter',
                    cellClass: 'align-left',
                  },
                  {
                    field: 'clientsTotal',
                    headerName: 'Clients Total',
                  },
                  {
                    field: 'dataSiteProvider_Name',
                    headerName: 'Data Site Provider',
                    filter: 'agTextColumnFilter',
                  },
                  {
                    field: 'primaryAccess_Name',
                    headerName: 'Primary Access Name',
                    filter: 'agTextColumnFilter',
                    cellRenderer: 'primaryAccessCellRenderer',
                  },
                  {
                    field: 'primaryAccess_DataSiteEmail',
                    headerName: 'Primary Access Email',
                    filter: 'agTextColumnFilter',
                  },
                  {
                    field: 'secondaryAccess_Name',
                    headerName: 'Secondary Access Name',
                    filter: 'agTextColumnFilter',
                    cellRenderer: 'secondaryAccessCellRenderer',
                  },
                  {
                    field: 'secondaryAccess_DataSiteEmail',
                    headerName: 'Secondary Access Email',
                    filter: 'agTextColumnFilter',
                  },
                  {
                    field: 'fitchIndustry',
                    headerName: 'Fitch Industry',
                    filter: 'agTextColumnFilter',
                  },
                  {
                    field: 'creditSightsCompanyId',
                    headerName: 'CreditSights Company ID',
                    filter: 'agTextColumnFilter',
                  },
                ]}></AgGridReact>
            </div>
          </div>
        ) : (
          <NoDataFound dataType="companies" />
        )}
        {companyToDelete && (
          <Modal title="" active={deleteModalActive} onClose={handleCloseModal}>
            <p>Are you sure you want to delete {companyToDelete.name}?</p>
            <div className="button_side_wrap">
              <BixButton type="button" onClick={handleDeleteCompany}>
                Delete Company
              </BixButton>
              <BixButton light type="button" onClick={handleCloseModal}>
                Cancel
              </BixButton>
            </div>
          </Modal>
        )}

        <CreateCompany
          onCloseModal={handleCloseModal}
          isCreatingCompany={isCreatingCompany}
          defaultCompanyType="private"
        />
        <BulkImportCompanies
          active={isImportingCompanies}
          onImportSubmit={handleImportSubmit}
          onCloseModal={handleCloseImportModal}
        />
        <ImportLFIIds
          active={isImportingLFIIds}
          onImportSubmit={handleLFIImportSubmit}
          onCloseModal={handleCloseLFIImportModal}
        />
      </div>
    );
  }
};

const mapStateToProps = (state: AppState) => {
  const { companies } = getAllPrivateAdminCompaniesState(state);
  const { isCompanyMNPIInfoAdmin } = getIsCurrentUserCompanyMNPIInfoAdminState(state);
  return {
    companies,
    isLoading: state.companyState.isLoading,
    error: state.companyState.error,
    company: state.companyState.currentCompany,
    isCompanyMNPIInfoAdmin,
  };
};

const mapDispatchToProps = (dispatch: any) => ({
  dispatchGetCompanies: () => dispatch(getCompanies()),
  dispatchRemoveCompany: (company: Bixby.Models.AdminCompanySlim) => dispatch(removeCompany(company)),
});

export default connect(mapStateToProps, mapDispatchToProps)(CompanyList);
